/**
 * InviteSetPassword - the invite page view when the invite email link carries Clerk's
 * one-time ticket (__clerk_ticket). Shown instead of the normal invite page body.
 *
 * v4.29 - 2026-10-13 - INVITE-ACCEPT-FLOW-01 (B.1 in the dispatch): a brand-new invitee
 *                      lands here, sets a password with the ticket (no emailed code), the
 *                      invite is accepted and the person is taken into the inviting company.
 *                      Email is read-only (decoded from the ticket payload). No company is
 *                      ever asked. The customer-facing text that only fits a new company
 *                      owner (such as "Start your free trial today") is not shown.
 *
 * No role string and no customer text lives in this file: every string reads from
 * teamAccess.setPassword.* (common.json). Logs carry invite_id, outcome and reason only;
 * never the ticket, never the invited email.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignUp } from '@clerk/clerk-react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { logClientDecision, setChosenCompany } from '../services/portalRequest';
import { teamService, InviteValidationResponse } from '../services/api';

interface InviteSetPasswordProps {
  /** The team-invite token from the URL path (/invite/<token>). */
  token: string;
  /** The one-time ticket Clerk added to the invite link as ?__clerk_ticket=... */
  ticket: string;
  /** The server's validate response for the invite (companyName, inviteId, role, …). */
  status: InviteValidationResponse;
}

/** Pure helper: decode the email claim from a Clerk ticket (a JWT) without verifying it.
 * The server verifies it when signUp.create is called; the client reads it only to show
 * the invited email read-only. Returns `null` if the ticket is not a decodable JWT.
 */
export function decodeInvitedEmail(ticket: string): string | null {
  try {
    const parts = ticket.split('.');
    if (parts.length < 2) return null;
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    // atob is available in every browser; Jest's jsdom environment also provides it.
    const json = typeof atob === 'function' ? atob(padded + padding) : Buffer.from(padded + padding, 'base64').toString('utf-8');
    const payload = JSON.parse(json);
    // Clerk's ticket payload names the email in `sid`/`sub`/`email`/`iss` depending on version;
    // read the first non-empty value from the usual places.
    const candidates = [payload.email, payload.email_address, payload.identifier];
    for (const c of candidates) {
      if (typeof c === 'string' && c.includes('@')) return c;
    }
    return null;
  } catch {
    return null;
  }
}

const InviteSetPassword: React.FC<InviteSetPasswordProps> = ({ token, ticket, status }) => {
  const { t } = useTranslation();
  const { signUp, setActive, isLoaded } = useSignUp();

  const invitedEmail = useMemo(() => decodeInvitedEmail(ticket), [ticket]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const inviteId = status.inviteId;
  const companyName = status.companyName || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    setSubmitting(true);
    setErrorCode(null);
    logClientDecision('invite_set_password_submitted', { invite: inviteId || 'unknown' });
    try {
      // Clerk's ticket strategy: the ticket replaces the emailed verification code, and
      // the email on the ticket is the account's email.
      const result = await signUp.create({
        strategy: 'ticket' as any,
        ticket,
        password,
        firstName,
        lastName,
      } as any);
      if (result.status !== 'complete' || !result.createdSessionId) {
        setErrorCode('ticketInvalid');
        logClientDecision('invite_set_password_refused', { reason: 'signup_incomplete', invite: inviteId || 'unknown' });
        return;
      }
      try {
        await setActive({ session: result.createdSessionId });
      } catch {
        setErrorCode('sessionStart');
        logClientDecision('invite_set_password_refused', { reason: 'session_start_failed', invite: inviteId || 'unknown' });
        return;
      }
      // The portal's axios interceptor adds X-Authorization from the active Clerk session;
      // accept the invite immediately so the person lands in the inviting company.
      const accept = await teamService.acceptInvite(token);
      if (accept.success && accept.tenantUuid) {
        setChosenCompany(accept.tenantUuid, result.createdUserId || null);
        logClientDecision('invite_set_password_accepted', { company: accept.tenantUuid, invite: inviteId || 'unknown' });
        window.location.assign('/dashboard');
        return;
      }
      setErrorCode(accept.error || 'generic');
      logClientDecision('invite_set_password_accept_refused', { reason: accept.error || 'unknown', invite: inviteId || 'unknown' });
    } catch (err: any) {
      const code = err?.errors?.[0]?.code || err?.code || '';
      let mapped: string = 'generic';
      if (code === 'form_password_pwned' || code === 'form_password_length_too_short') mapped = 'weakPassword';
      else if (code === 'form_param_format_invalid' || code === 'ticket_invalid' || code === 'ticket_expired' || code === 'invitation_identifier_mismatch') mapped = 'ticketInvalid';
      setErrorCode(mapped);
      logClientDecision('invite_set_password_refused', { reason: code || 'exception', invite: inviteId || 'unknown' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoaded) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#003366', py: 2, px: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>MyLojic</Typography>
      </Box>
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <GroupAddIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {t('teamAccess.setPassword.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('teamAccess.setPassword.subtitle', { company: companyName })}
            </Typography>
          </Box>

          {errorCode && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(`teamAccess.setPassword.errors.${errorCode}`, { defaultValue: t('teamAccess.setPassword.errors.generic') })}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {invitedEmail && (
              <TextField
                fullWidth
                label={t('signup.emailLabel', { defaultValue: 'Email' })}
                value={invitedEmail}
                helperText={t('teamAccess.setPassword.emailHelp', { email: invitedEmail })}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
                inputProps={{ 'aria-readonly': true }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                required
                autoFocus
                label={t('teamAccess.setPassword.firstNameLabel')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting}
              />
              <TextField
                fullWidth
                required
                label={t('teamAccess.setPassword.lastNameLabel')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
              />
            </Box>
            <TextField
              fullWidth
              required
              type="password"
              label={t('teamAccess.setPassword.passwordLabel')}
              helperText={t('teamAccess.setPassword.passwordRequirements')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              autoComplete="new-password"
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={submitting || !firstName.trim() || !lastName.trim() || !password}
              sx={{ bgcolor: '#008080', py: 1.5, '&:hover': { bgcolor: '#006666' } }}
            >
              {submitting ? <CircularProgress size={22} color="inherit" /> : t('teamAccess.setPassword.submit')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default InviteSetPassword;
